import { AllowedExperimentKey } from '@tenants/types';
import { type ReactNode, memo } from 'react';
import { useTenantId } from 'hooks/use-tenant-id';
import { useExperimentTracking, useExperimentVariation } from './hooks';

export interface ExperimentalSectionProps {
  experimentKey: AllowedExperimentKey;
  control: ReactNode;
  variation: ReactNode;
}

/**
 * Renders control or variation based on SSR-evaluated experiment results.
 * 
 * IMPORTANT: Tenant targeting is configured in two places:
 * 1. GrowthBook (primary targeting)
 * 2. Tenant config features.Experiments array (code-level safety layer)
 * 
 * The experiment will only run if BOTH conditions are met:
 * - GrowthBook evaluates the user into the variation
 * - The experiment key is listed in tenant.features.Experiments
 * 
 * @see docs/guides/experiments.md - Full setup guide
 * 
 * @example
 * ```tsx
 * <ExperimentalSection
 *   experimentKey={AllowedExperimentKey.LISTING_HERO}
 *   control={<LegacyHero listing={listing} />}
 *   variation={<NewHero listing={listing} />}
 * />
 * ```
 * 
 * Debug: 
 * - Use ModHeader with `x-gb-force: experiment-key:1` to force variation (works in all environments)
 * - Use URL parameter `?gb_force_experiment-key=1` to force variation (only works in development and preview environments)
 */
export const ExperimentalSection = memo(function ExperimentalSection({ 
  experimentKey, 
  control,
  variation,
}: ExperimentalSectionProps) {
  // Get tenant ID for tracking
  const tenantId = useTenantId();
  
  // Determine if variation should be shown (SSR already evaluated URL/header overrides)
  const { showVariation, variationId } = useExperimentVariation(experimentKey);
  
  // Track analytics (circuit breaker already tracked the decision during SSR)
  useExperimentTracking(experimentKey, variationId, tenantId);

  return showVariation ? variation : control;
});
